import { z } from 'zod';
import { imageWithUploadSchema } from './image.schema';
import { legalEntitySchema } from './legal.schema';
import { bankCommonInfoSchema } from './bank.schema';
import { orgSettingsSchema, orgProfileSettingsUpdateSchema } from '../model/org/org.settings.model';
import { orgFinancingProfileUpsertSchema } from '../model/financing/financing.profile.model';
import { nameSchema } from '../validation';

/**
 * `orgProfileUpdateSchema` — section-keyed, field-level partial body
 * for `PATCH /org/profile`.
 *
 * Design contract (see `docs/relations-v2/...` and the PR that
 * replaced the legacy `organization/profile` mapper):
 *
 * 1. **Field-level partial merge**. Both the section keys AND the keys
 *    inside each section are independently optional. Anything you don't
 *    send stays as-is on the server. `{ bank: { IBAN: 'SA…' } }` is a
 *    complete request — it updates only `Bank.IBAN` and touches nothing
 *    else. The backend service mirrors this contract by reading
 *    `Object.keys(section)` and writing only those keys through the
 *    DB layer's default dotted-path merge (no `overwriteAnyNestedField`
 *    spec — see `apps/backend/src/app/db/db.types.ts` for the rule
 *    that prevents the "Bank save wipes Legal" class of bug).
 *
 * 2. **Empty string never overwrites**. Every present-string field
 *    enforces `.trim().min(1)` so a stray `displayName: ''` from the
 *    UI gets rejected here, before it can ever clobber `Org.name`.
 *    Schema-layer enforcement means the rule holds for every caller
 *    (web, mobile, admin tools, future scripts).
 *
 * 3. **`undefined` = "don't touch"**. To intentionally CLEAR a value
 *    we'd add `.nullable()` on a per-field basis — out of scope for
 *    this PR; today every field is "set or leave alone".
 *
 * 4. **`financingProfile` rides on the same endpoint**. The pilot
 *    initially had its own `PATCH /financing/profile` route; the MVP
 *    cleanup folded it back here so `financialStatements[]` flow
 *    through the same `imageWithUpload` pipeline as `legal.*Image`
 *    and `bank.accountImage`. The financing module no longer owns
 *    a profile-write surface — the carrier dashboard updates the
 *    profile via this endpoint.
 */
export const orgProfileUpdateSchema = z.object({
  /**
   * `Org.name`. When present, must be non-empty after trim — empty
   * string never overwrites the existing display name.
   * `nameSchema` is `z.coerce.string().trim().min(1)` already, so
   * `.optional()` is enough.
   */
  displayName: nameSchema.optional(),

  /**
   * `Org.logo`. Single image, same `imageWithUpload` shape used by
   * `legal.*Image` so the backend `imageService` can materialize a
   * Cloudinary asset before the DB write.
   */
  logo: imageWithUploadSchema.optional(),

  /**
   * Legal entity sub-doc partial — every field within is optional;
   * single-field patches like `{ legal: { taxId: 'X' } }` work and
   * leave CR, address, license, and tax-image untouched.
   * `legalEntitySchema` already wraps identity strings (`registrationId`,
   * `registrationName`, `taxId`, `licenseId`, `fullAddress`, `addressCity`)
   * in `optionalSparse(...)`, which strips blank input to `undefined`
   * BEFORE running the inner `min(1)` check — so an empty-string
   * `taxId` field in the body becomes a no-op rather than wiping the
   * stored value.
   */
  legal: legalEntitySchema.partial().optional(),

  /**
   * Bank sub-doc partial — every field within is optional. Backend
   * service enforces the create-vs-update branching: when a primary
   * `Bank` doc already exists, any subset of fields is a valid
   * partial update. When NO bank exists, all four create-fields
   * (`bankName`, `IBAN`, `accountName`, `accountImage`) must be
   * present; the service throws `bank_partial_update_requires_existing_bank`
   * with the missing-field list otherwise.
   *
   * Identity strings (`IBAN`, `bankName`, `accountName`) inherit
   * the underlying tight schemas (e.g. `ibanNumberSchema` with its
   * 22-char min + SA-prefix transform) when present.
   */
  bank: bankCommonInfoSchema.partial().optional(),

  /**
   * Unified `Org.settings` partial. Top-level `.partial()` is enough
   * because every inner key is already optional. Single-key patches
   * supported.
   */
  settings: orgProfileSettingsUpdateSchema.optional(),

  /**
   * `Org.financingProfile` upsert payload. Every field on the
   * profile is independently optional in the inbound shape; the
   * server fills `updatedAt` and stamps `Org.financingProfile`
   * wholesale. `financialStatements[]` accepts the `imageWithUpload`
   * shape, so the carrier dashboard can POST `{ upload: '<base64>' }`
   * entries side-by-side with already-uploaded `{ id, url }` ones
   * and the backend's `ImageService.financingProfile()` walker
   * materialises them inline (no separate upload round-trip).
   */
  financingProfile: orgFinancingProfileUpsertSchema.optional(),
});

export type OrgProfileUpdate = z.infer<typeof orgProfileUpdateSchema>;
